/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { AiManagedObject, AiManagedObjectConfig } from '@atlassian/ai-managed-objects/types';
import { validateManagedBlockConfig } from '@atlassian/ai-managed-objects/validation';

import { messages } from '../../common/messages';

import { AimoStatusMetadata } from './AimoStatusMetadata';
import { ConfigPanel } from './ConfigPanel';
import {
	arePanelConfigurationsEqual,
	type ManagedBlockConfigSaveHandler,
	type ManagedBlockConfiguration,
	type ManagedBlockConfigurationUpdate,
	getDefaultStructuredPanelConfiguration,
	hasPersistedConfigurationChanged,
	toAiManagedObjectConfigInput,
} from './configuration';
import { ManagedBlockConfigFrame } from './ManagedBlockConfigFrame';
import { PromptPanel } from './PromptPanel';
import type { SmartCardProviderProduct } from './sources/smart-card-product';

type SourceValidationError = 'invalid' | 'pending' | 'required' | null;

const saveErrorStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	color: token('color.text.danger'),
});

const footerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const actionsStyles = css({
	marginLeft: 'auto',
});

type ManagedBlockConfigProps = {
	/** Hydrated AI-managed object metadata for the configured block. */
	aimoData?: AiManagedObject | null;
	/** Site cloud ID, used for profilecard hydration. */
	cloudId?: string;
	/** Current Confluence page URL to use for current-page sources. */
	currentPageSourceUrl?: string;
	/** If set, an error message to display inline at the bottom of the panel. */
	error?: string | null;
	/** Initial configuration hydrated from the persisted AI-managed object. */
	initialConfiguration: ManagedBlockConfiguration;
	/** If true, the panel is currently performing an async operation. */
	isSaving?: boolean;
	/** Called when the user dismisses the panel. */
	onCancel?: () => void;
	/** Called when the user changes config after an error is shown. */
	onClearError?: () => void;
	/** Called when the user clicks save with normalized AIMO config input. */
	onSave: ManagedBlockConfigSaveHandler;
	/** Persisted configuration used to disable no-change saves. */
	persistedConfig?: AiManagedObjectConfig | null;
	/** Product context supplied to Smart Links when rendering configured source chips. */
	smartCardProviderProduct?: SmartCardProviderProduct;
};

/**
 * Inline "Configure block" panel rendered directly underneath an
 * agent-managed bodied extension.
 */
export const ManagedBlockConfig = ({
	aimoData = null,
	cloudId,
	currentPageSourceUrl,
	initialConfiguration,
	onSave,
	onCancel,
	onClearError,
	isSaving = false,
	error = null,
	persistedConfig = null,
	smartCardProviderProduct,
}: ManagedBlockConfigProps): JSX.Element => {
	const intl = useIntl();
	const [configuration, setConfiguration] =
		useState<ManagedBlockConfiguration>(initialConfiguration);
	const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
	const hasEditedConfigurationRef = useRef(false);
	const hasEditedPromptRef = useRef(false);
	const lastHydratedConfigurationRef = useRef(initialConfiguration);
	const configurationRef = useRef(configuration);

	useEffect(() => {
		configurationRef.current = configuration;
	}, [configuration]);

	useEffect(() => {
		const currentConfiguration = configurationRef.current;
		const lastHydratedConfiguration = lastHydratedConfigurationRef.current;
		lastHydratedConfigurationRef.current = initialConfiguration;

		if (
			arePanelConfigurationsEqual(
				currentConfiguration,
				lastHydratedConfiguration,
				currentPageSourceUrl,
			)
		) {
			hasEditedConfigurationRef.current = false;
			hasEditedPromptRef.current = false;
			setConfiguration(initialConfiguration);
		}
	}, [currentPageSourceUrl, initialConfiguration]);

	const clearErrorsOnInteraction = useCallback(() => {
		setHasAttemptedSave(false);
		onClearError?.();
	}, [onClearError]);

	const markConfigurationEdited = useCallback(() => {
		hasEditedConfigurationRef.current = true;
	}, []);

	const handleConfigurationChange = useCallback((update: ManagedBlockConfigurationUpdate) => {
		setConfiguration(update);
	}, []);

	const handlePromptToggle = useCallback(() => {
		const currentMode = configurationRef.current.mode;
		const shouldResetConfiguration = currentMode === 'prompt' && hasEditedPromptRef.current;
		const shouldResetPrompt = currentMode === 'configuration' && hasEditedConfigurationRef.current;

		clearErrorsOnInteraction();
		setConfiguration((currentConfiguration) => {
			if (currentConfiguration.mode === 'prompt') {
				if (shouldResetConfiguration) {
					return getDefaultStructuredPanelConfiguration(currentConfiguration);
				}

				return {
					...currentConfiguration,
					mode: 'configuration',
				};
			}

			if (shouldResetPrompt) {
				return {
					...currentConfiguration,
					mode: 'prompt',
					prompt: '',
				};
			}

			return {
				...currentConfiguration,
				mode: 'prompt',
			};
		});

		if (shouldResetConfiguration || shouldResetPrompt) {
			hasEditedConfigurationRef.current = false;
			hasEditedPromptRef.current = false;
		}
	}, [clearErrorsOnInteraction]);

	const handlePromptChange = useCallback(
		(nextPrompt: string) => {
			clearErrorsOnInteraction();

			if (configurationRef.current.prompt !== nextPrompt) {
				hasEditedPromptRef.current = true;
			}

			setConfiguration((currentConfiguration) => ({
				...currentConfiguration,
				prompt: nextPrompt,
			}));
		},
		[clearErrorsOnInteraction],
	);

	const configInput = useMemo(
		() => toAiManagedObjectConfigInput(configuration, currentPageSourceUrl),
		[configuration, currentPageSourceUrl],
	);
	const validation = useMemo(() => validateManagedBlockConfig(configInput), [configInput]);
	const manualSourceValidationError = useMemo<SourceValidationError>(() => {
		if (configuration.mode !== 'configuration' || configuration.sourceMode !== 'manual') {
			return null;
		}

		if (configuration.sourceEntries.length === 0) {
			return 'required';
		}

		if (configuration.sourceEntries.some((source) => source.status === 'resolving')) {
			return 'pending';
		}

		if (configuration.sourceEntries.some((source) => source.status === 'invalid')) {
			return 'invalid';
		}

		if (!configuration.sourceEntries.some((source) => source.status === 'resolved')) {
			return 'required';
		}

		return null;
	}, [configuration.mode, configuration.sourceEntries, configuration.sourceMode]);
	const sourceValidationError =
		hasAttemptedSave &&
		configuration.mode === 'configuration' &&
		configuration.sourceMode === 'manual'
			? manualSourceValidationError
			: null;
	const isAdditionalContextInvalid =
		configuration.mode === 'configuration' &&
		configuration.isAdditionalContextSelected &&
		configuration.additionalContext.trim().length === 0;
	const hasConfigurationChanged = hasPersistedConfigurationChanged(persistedConfig, configInput);
	const isSaveDisabled = isSaving || !hasConfigurationChanged || isAdditionalContextInvalid;

	const handleSave = useCallback(() => {
		if (isSaveDisabled) {
			return;
		}

		setHasAttemptedSave(true);

		if (manualSourceValidationError || !validation.valid) {
			return;
		}

		onSave(configInput);
	}, [configInput, isSaveDisabled, manualSourceValidationError, onSave, validation.valid]);

	return (
		<ManagedBlockConfigFrame testId="agent-managed-extension-configure-panel">
			{configuration.mode === 'configuration' && (
				<ConfigPanel
					additionalContextValidationError={isAdditionalContextInvalid}
					configuration={configuration}
					isDisabled={isSaving}
					onChange={handleConfigurationChange}
					onConfigEdit={markConfigurationEdited}
					onInteraction={clearErrorsOnInteraction}
					smartCardProviderProduct={smartCardProviderProduct}
					sourceValidationError={sourceValidationError}
				/>
			)}

			<PromptPanel
				isDisabled={isSaving}
				isExpanded={configuration.mode === 'prompt'}
				onPromptChange={handlePromptChange}
				onToggle={handlePromptToggle}
				prompt={configuration.prompt}
			/>

			{error && (
				<div css={saveErrorStyles} data-testid="agent-managed-extension-error" role="alert">
					<StatusErrorIcon label="" color="currentColor" size="small" />
					<Text color="color.text.danger">{error}</Text>
				</div>
			)}
			<div css={footerStyles}>
				<AimoStatusMetadata aimoData={aimoData} cloudId={cloudId} />
				<div css={actionsStyles}>
					<Inline space="space.100" alignInline="end">
						{onCancel && (
							<Button appearance="subtle" onClick={onCancel} isDisabled={isSaving}>
								{intl.formatMessage(messages.cancel)}
							</Button>
						)}
						<Button
							appearance="primary"
							testId="agent-managed-extension-save"
							onClick={handleSave}
							isDisabled={isSaveDisabled}
							iconAfter={isSaving ? () => <Spinner size="small" /> : undefined}
						>
							{intl.formatMessage(messages.save)}
						</Button>
					</Inline>
				</div>
			</div>
		</ManagedBlockConfigFrame>
	);
};
